<template>
  <!-- Nothing at all for a lab outside the beta. An always-present control that
       always refuses is worse than no control: it advertises a feature the user
       cannot have and costs a tap to find that out. -->
  <div v-if="available">
    <button
      type="button"
      class="sa-voice-fab"
      :class="{ 'sa-voice-fab-spent': exhausted }"
      :aria-label="label"
      :title="label"
      @click="open"
    >
      <svg
        viewBox="0 0 24 24"
        width="26"
        height="26"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
      >
        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
        <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
        <line x1="12" y1="19" x2="12" y2="23" />
      </svg>
      <span class="sa-voice-fab-word">Voice</span>
    </button>

    <voice-command-modal v-if="modalOpen" @close="modalOpen = false" />
  </div>
</template>

<script>
import VoiceCommandModal from "./VoiceCommandModal.vue";

export default {
  name: "VoiceControl",
  components: { VoiceCommandModal },
  data() {
    return { modalOpen: false };
  },
  computed: {
    credits() {
      return this.$store.state.voiceCredits;
    },
    // Absent credits means the check has not come back yet, or failed. Either
    // way the button stays hidden rather than appearing and then vanishing.
    available() {
      return !!this.credits && this.credits.voiceEnabled === true;
    },
    exhausted() {
      return !!this.credits && this.credits.exhausted === true;
    },
    label() {
      if (this.exhausted) return "Voice control — no minutes left this period";
      return "Start voice control";
    },
  },
  methods: {
    open() {
      if (this.exhausted) {
        // Answered locally. The mint endpoint would refuse this with the same
        // message; asking it first would just be a slower way to say no.
        this.$store.dispatch("notify", {
          uiState: "warning",
          title: "Voice not started.",
          detail: "This lab has used all its voice minutes this period.",
          next: "Ask your lab admin to raise the allowance.",
          key: "voice|exhausted",
        });
        return;
      }
      this.modalOpen = true;
    },
  },
  mounted() {
    // Swallowed on purpose: a lab that has never heard of voice must not get an
    // error card on every visit to Manage Products just because this endpoint
    // is not deployed yet. No credits means no button, and nothing said.
    this.$store.dispatch("fetchVoiceCredits").catch(() => {});
  },
};
</script>

<style scoped>
/* Plain CSS. public/assets/styles.css is pre-compiled and committed, so a
   Tailwind class not already in it does nothing at runtime. */

.sa-voice-fab {
  position: fixed;
  right: 0.875rem;
  /* Sits in the thumb arc, clear of the iOS home indicator. */
  bottom: calc(1rem + env(safe-area-inset-bottom, 0px));
  /* Above the page, below the nav overlay (9000) and notifications (10000). */
  z-index: 40;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;

  /* 56 px: above the 52 px preferred gloved-hand target. */
  min-width: 56px;
  min-height: 56px;
  padding: 0 0.5rem;

  border: 2px solid #0c4a6e;
  border-radius: 9999px;
  background: #0c4a6e;
  color: #ffffff;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.2),
    0 2px 4px -2px rgba(0, 0, 0, 0.1);
}

/* Out of minutes. Still visible and still tappable — it explains itself when
   pressed — but no longer presented as the ready primary action. */
.sa-voice-fab-spent {
  background: #ffffff;
  color: #0c4a6e;
}

.sa-voice-fab-word {
  line-height: 1;
}

.sa-voice-fab:focus-visible {
  outline: 3px solid #0c4a6e;
  outline-offset: 2px;
}
</style>
